import { useMutation } from '@apollo/client'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'

import {
  DELETE_ACTION_GENERATOR,
  DeleteActionGeneratorMutation,
  DeleteActionGeneratorMutationVariables,
} from '@/graphql'
import useT from '@/i18n/useT'
import { DateHelpers } from '@/utils/date.helpers'

type Props = {
  ids?: string[] | null
  open: boolean
  actionTitle?: string
  actionDueTime?: string
  onClose: () => void
  onSuccess?: () => void
}

export const DeleteActionGenerator: React.FC<Props> = props => {
  const { ids, open, onClose, onSuccess, actionTitle, actionDueTime } = props
  const { t } = useT()

  const [deleteAction, { error, loading }] = useMutation<
    DeleteActionGeneratorMutation,
    DeleteActionGeneratorMutationVariables
  >(DELETE_ACTION_GENERATOR)

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>{t('deleteActionGenerator.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={4}>
          {error && <Alert severity={'warning'}>{error.message}</Alert>}
          <Box sx={{ height: 2, width: '100%' }} />
        </Stack>
        <Typography>{t('deleteActionGenerator.confirmation')}</Typography>
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="subtitle1">{actionTitle}</Typography>
            {actionDueTime && (
              <Typography variant="subtitle2">
                {t('taskPage.dueTime', {
                  time: DateHelpers.formatTime(actionDueTime),
                })}
              </Typography>
            )}
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => {
            onClose()
          }}
          disabled={loading}
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          loading={loading}
          loadingPosition="end"
          disabled={loading}
          onClick={async () => {
            const promises = ids?.map(id =>
              deleteAction({ variables: { input: { id } } }),
            )
            await Promise.all(promises || [])
            onSuccess?.()
          }}
        >
          {t('deleteActionGenerator.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
